import { Button } from '@civilgrid/ui/components';
import { isEmpty } from 'lodash';
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import type { TOCChild, TOCParent } from '~/common/types';
import { Checkbox } from '~/components2/Checkbox';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import Input from '~/components2/forms/Input';
import Icon from '~/components2/Icon';
import layerStore, { DEFAULT_METADATA_IDS } from '~/store2/layers';
import { isSupportedMetadataLayer } from '~/store2/TOCLayers';
import { METADATA_PICKER_PANEL_ID, toPanelId } from './panelIds';
import { isTOCParent } from './TOC';

function matchSearch(str1: string, str2: string) {
  return str1.toLowerCase().includes(str2.toLowerCase());
}

// Sections can share a label, both across branches and between siblings, so expansion state and
// panel ids key on the parent chain and prefer the catalog node id over the label.
function sectionPath(parentPath: string, item: TOCParent) {
  const segment = item.sectionId ?? item.label;
  return parentPath ? `${parentPath}>${segment}` : segment;
}

export default function MetadataPicker() {
  const { visibleLayerIds, tilesConfig, TOCLayers, visibleMetadataIds } = useSnapshot(layerStore) as typeof layerStore;
  const [searchTerm, setSearchTerm] = useState('');

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const panelRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();

  // Take focus only when opened from the Layer Attributes button, and hand it back on close while it's
  // still inside; the cleanup runs before React removes the panel, so a focused close button still counts.
  useLayoutEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    if (opener?.getAttribute('aria-controls') !== METADATA_PICKER_PANEL_ID) return;
    titleRef.current?.focus();
    const panel = panelRef.current;
    return () => {
      if (panel?.contains(document.activeElement) && opener.isConnected) opener.focus();
    };
  }, []);

  // expanded sections by default whenever visibleLayerIds change
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const visibleSections = new Set<string>();

    const collectVisibleSections = (item: TOCParent, parentPath = '') => {
      if (
        item.children.some(
          (child) => isTOCParent(child) || (visibleLayerIds.has(child.id) && isSupportedMetadataLayer(child.id)),
        )
      ) {
        const nodePath = sectionPath(parentPath, item);
        visibleSections.add(nodePath);
        item.children.forEach((child) => {
          if (isTOCParent(child)) collectVisibleSections(child, nodePath);
        });
      }
    };

    TOCLayers.forEach((item) => {
      if (isTOCParent(item)) collectVisibleSections(item);
    });

    setExpandedSections(visibleSections);
  }, [visibleLayerIds]);

  const toggleSection = (nodePath: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodePath)) {
        newSet.delete(nodePath);
      } else {
        newSet.add(nodePath);
      }
      return newSet;
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const sections = useMemo(() => {
    if (!tilesConfig) {
      return null;
    }

    const sections: any[] = [];

    const renderParent = (item: TOCParent, ignoreFilter = false, parentPath = '') => {
      const shouldIgnoreFilter = !isEmpty(searchTerm) && matchSearch(item.label, searchTerm);

      const nodePath = sectionPath(parentPath, item);
      const isExpanded = expandedSections.has(nodePath) || shouldIgnoreFilter;
      const panelId = toPanelId('metadata-picker', nodePath);

      const children = item.children
        .map((child) => {
          if (isTOCParent(child)) {
            return renderParent(child, ignoreFilter || shouldIgnoreFilter, nodePath);
          } else {
            return renderChild(child, ignoreFilter || shouldIgnoreFilter);
          }
        })
        .filter((item) => item !== null);

      if (children.length > 0) {
        return (
          <div key={nodePath} className="my-2 ml-2.5">
            <button
              type="button"
              data-testid={toPanelId('metadata-picker-section-expand', nodePath)}
              className="inline-flex appearance-none flex-row items-center space-x-1 rounded border-0 bg-transparent p-0 hover:bg-gray-100"
              onClick={() => toggleSection(nodePath)}
              aria-expanded={isExpanded}
              aria-controls={isExpanded ? panelId : undefined}
            >
              <Icon icon={isExpanded ? 'chevronDown' : 'chevronRight'} />
              <span>{item.label}</span>
            </button>
            <div id={panelId} className={isExpanded ? '' : 'hidden'}>
              {children}
            </div>
          </div>
        );
      } else {
        return null;
      }
    };

    const renderChild = (item: TOCChild, ignoreFilter = false) => {
      if (visibleLayerIds.has(item.id) && isSupportedMetadataLayer(item.id)) {
        const layerConfig = tilesConfig[item.id];
        if (layerConfig) {
          const properties = Object.keys(layerConfig.fields)
            .map((k) => {
              if (k === 'OBJECTID') {
                return null;
              }

              const label = layerConfig.fields[k].alias;

              if (
                matchSearch(label, searchTerm) ||
                ignoreFilter ||
                isEmpty(searchTerm) ||
                (!isEmpty(searchTerm) && matchSearch(item.label, searchTerm))
              ) {
                return (
                  <div key={k} className="p-1">
                    <Checkbox
                      data-testid={`metadata-picker-field-${item.id}.${k}`}
                      checked={visibleMetadataIds.has(`${item.id}.${k}`)}
                      onChange={(isVisible) => {
                        if (isVisible) {
                          layerStore.visibleMetadataIds.add(`${item.id}.${k}`);
                        } else {
                          layerStore.visibleMetadataIds.delete(`${item.id}.${k}`);
                        }
                      }}
                      text={label}
                    />
                  </div>
                );
              } else {
                return null;
              }
            })
            .filter((p) => p !== null);
          if (properties.length > 0) {
            return (
              <div key={item.id} className="my-2">
                <div className="my-2 ml-2.5 font-medium">{item.label}</div>
                <div className="ml-5 flex flex-col">{properties}</div>
              </div>
            );
          } else {
            return null;
          }
        } else {
          return null;
        }
      } else {
        return null;
      }
    };

    TOCLayers.forEach((item) => {
      if (isTOCParent(item)) {
        sections.push(renderParent(item));
      } else {
        sections.push(renderChild(item));
      }
    });

    const validSections = sections.filter((s) => s !== null);
    if (searchTerm.length > 0 && validSections.length === 0) {
      return <div className="p-2">Search did not return any results.</div>;
    } else {
      return validSections;
    }
  }, [TOCLayers, tilesConfig, visibleLayerIds, visibleMetadataIds.size, searchTerm, expandedSections]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const defaultIdsSelected = useMemo(() => {
    return (
      visibleMetadataIds.size === DEFAULT_METADATA_IDS.length &&
      DEFAULT_METADATA_IDS.every((id) => visibleMetadataIds.has(id))
    );
  }, [visibleMetadataIds.size]);

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: Escape from any control inside closes the dialog
    <div
      ref={panelRef}
      id={METADATA_PICKER_PANEL_ID}
      role="dialog"
      aria-labelledby={titleId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') layerStore.toggleMetadataPicker(false);
      }}
      data-testid="metadata-picker-panel"
      className="pointer-events-auto absolute left-[26rem] top-4 z-20 ml-6 flex max-h-[90%] w-[26rem] max-w-[80%] flex-col rounded-sm bg-white p-1 shadow-lg"
    >
      <div className="flex w-full items-center justify-between p-2">
        <h2 id={titleId} ref={titleRef} tabIndex={-1} className="rounded-sm focus-ring">
          Select Layer Attributes to Display
        </h2>

        <div className="flex items-center space-x-2">
          <CloseIconButton
            aria-label="Close layer attributes picker"
            onClick={() => layerStore.toggleMetadataPicker()}
            data-testid="metadata-picker-close-button"
          />
        </div>
      </div>

      {visibleLayerIds.size > 0 ? (
        <>
          <div className="flex flex-col space-y-2">
            <Input
              label=""
              placeholder="Search"
              data-testid="metadata-picker-search-input"
              onChange={(e) => {
                setSearchTerm(e);
              }}
              value={searchTerm}
            />

            <div className="grid grid-cols-2 space-x-2">
              {visibleMetadataIds.size > 0 && (
                <Button
                  variant="secondary"
                  data-testid="metadata-picker-clear-button"
                  onClick={() => layerStore.visibleMetadataIds.clear()}
                >
                  Clear Attributes
                </Button>
              )}
              {!defaultIdsSelected && (
                <Button
                  variant="secondary"
                  data-testid="metadata-picker-restore-defaults-button"
                  onClick={() => {
                    layerStore.visibleMetadataIds.clear();
                    DEFAULT_METADATA_IDS.forEach((id) => {
                      layerStore.visibleMetadataIds.add(id);
                    });
                  }}
                >
                  Restore Defaults
                </Button>
              )}
            </div>
          </div>
          <div className="my-1 overflow-y-auto pr-2">{sections}</div>
        </>
      ) : (
        <div className="p-4" data-testid="metadata-picker-empty-state">
          <div className="bg-brand-light p-2">Layers need to be toggled ON prior to selecting attributes.</div>
        </div>
      )}
    </div>
  );
}
